import React from 'react';
import { Button, ButtonProps } from 'antd';

interface CustomButtonProps extends ButtonProps {
  variant?: 'primary' | 'secondary' | 'neutral';
  children: React.ReactNode;
}

/**
 * CustomButtonComponent - A flat button using the core frontend CTA and secondary action colours.
 * @param variant 'primary' (orange CTA), 'secondary' (slate outline, fills on hover)
 * or 'neutral' (hairline outline for utility actions such as Refresh)
 */
export const CustomButtonComponent = ({ 
  variant = 'primary', 
  children, 
  style, 
  className, 
  ...props 
}: CustomButtonProps) => {
  const isPrimary = variant === 'primary';

  return (
    <>
      <Button
        className={`premium-button premium-btn-${variant} ${className || ''}`}
        type={isPrimary ? 'primary' : 'default'}
        style={{
          height: '36px',
          borderRadius: '8px',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'none',
          ...style,
        }}
        {...props}
      >
        {children}
      </Button>
      <style>{`
        .premium-button {
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease !important;
        }

        /* Primary variant = orange call to action */
        .premium-btn-primary:not(:disabled) {
          background: #f97316 !important;
          border-color: #f97316 !important;
          color: #fff !important;
        }

        .premium-btn-primary:hover:not(:disabled) {
          background: #ea580c !important;
          border-color: #ea580c !important;
        }

        .premium-btn-primary:active:not(:disabled) {
          background: #c2410c !important;
          border-color: #c2410c !important;
        }

        /* Secondary variant = slate outline */
        .premium-btn-secondary:not(:disabled) {
          border-color: #1e293b !important;
          color: #1e293b !important;
          background: transparent !important;
        }

        .premium-btn-secondary:hover:not(:disabled) {
          background: #1e293b !important;
          color: #fff !important;
        }

        /* Neutral variant = hairline outline, light tint on hover */
        .premium-btn-neutral:not(:disabled) {
          border-color: #d6dee8 !important;
          color: #334155 !important;
          background: #ffffff !important;
        }

        .premium-btn-neutral:hover:not(:disabled) {
          border-color: #d6dee8 !important;
          color: #0f172a !important;
          background: #f1f5f9 !important;
        }

        .premium-btn-neutral:active:not(:disabled) {
          background: #e9eef4 !important;
        }

        .ant-btn:focus {
          box-shadow: none !important;
        }
      `}</style>
    </>
  );
};
